import React, { useEffect, useState } from 'react';
import './App.css';
import { Toaster } from 'react-hot-toast';
import Router from './router/Router';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentUser,
  selectDeviceType,
  selectDeviceVersion,
  selectFcmToken,
  setDeviceType,
  setDeviceVersion,
  setFcmToken,
} from './features/Auth/authSlice';
import { toggleSwipe } from './features/Search/searchSlice';
import ErrorBoundary from './utils/ErrorBoundary';
import UpdateDialog from './utils/UpdateDialog';
import {
  useGetVersionQuery,
  useLogoutMutation,
  useReLoginMutation,
  useRemoveFcmTokenMutation,
} from './features/Auth/authApiSlice';
import { clearAuth } from './features/Auth/Utility/ClearAuth';
import { ErrorPage } from './utils/ErrorPage';

function App() {
  if (process.env.NODE_ENV === 'development') {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const reactErrorPattern = /The above error occurred|React has detected/;
      if (args?.length && typeof args[0] === 'string' && reactErrorPattern?.test(args[0])) {
        return;
      }
      originalConsoleError(...args);
    };
  }

  const dispatch = useDispatch();
  const [flag, setFlag] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  const user = useSelector(selectCurrentUser);
  const deviceType = useSelector(selectDeviceType);
  const deviceVersion = useSelector(selectDeviceVersion);
  const FCMToken = useSelector(selectFcmToken);

  const [isReady, setIsReady] = useState(!window.ReactNativeWebView);
  const [deviceInfo, setDeviceInfo] = useState(false);

  const { data: getVersion } = useGetVersionQuery(deviceType || 'ios', {
    skip: !deviceType,
  });
  const [reLogin] = useReLoginMutation();
  const [logout] = useLogoutMutation();
  const [removeFCMToken] = useRemoveFcmTokenMutation();

  const handleLogout = async () => {
    if (!user || !user?.empId) return;
    await removeFCMToken({ empId: user?.empId, deviceType }).unwrap();
    logout();
    clearAuth(dispatch);
  };

  useEffect(() => {
    const handleReLogin = async () => {
      if (
        user?.username &&
        user?.password &&
        ![
          'admin',
          'vendor',
          'marcom',
          'canteenAdmin',
          'sports_social_celebration',
          'learning_development',
        ].includes(user.username)
      ) {
        const response = await reLogin({
          username: user.username,
          password: user.password,
          navigator: {
            userAgent: window.navigator.userAgent,
            language: window.navigator.language,
            platform: window.navigator.platform,
            vendor: window.navigator.vendor,
          },
          fcmToken: FCMToken,
        });

        if (response?.error?.status === 500) {
          handleLogout();
        }
      }
    };

    if (isReady && user) {
      handleReLogin();
    }
  }, [isReady, user]);

  useEffect(() => {
    window.ReactNativeWebView && window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'loaded', value: true }));

    setFlag((prev) => !prev);

    console.log(
      `%c
█░░ █ █▄░█ █▄▀ █░█░█ ▄▀█ █▄█
█▄▄ █ █░▀█ █░█ ▀▄▀▄▀ █▀█ ░█░ \n`,
      'font-family:monospace;color:#0a4fac;font-size:12px;'
    );

    dispatch(toggleSwipe({ value: false }));
  }, []);

  const parseVersion = (version) => {
    if (!version) return [];
    const match = version.match(/\d+(\.\d+)*$/);
    return match ? match[0].split('.').map(Number) : [];
  };

  const compareVersions = (serverVersion, installedVersion) => {
    const serverParts = parseVersion(serverVersion);
    const installedParts = parseVersion(installedVersion);

    for (let i = 0; i < Math.max(serverParts.length, installedParts.length); i++) {
      const a = serverParts[i] || 0;
      const b = installedParts[i] || 0;
      if (a > b) return 1;
      if (a < b) return -1;
    }
    return 0;
  };

  const isServerVersionGreater = (serverVersion, installedVersion) =>
    compareVersions(serverVersion, installedVersion) > 0;

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (data?.fcm) {
        if (data?.deviceInfo) {
          setDeviceInfo(true);
        }
        dispatch(setFcmToken(data?.fcm?.fcmtoken));
        dispatch(setDeviceType(data?.os));
        dispatch(setDeviceVersion(data?.deviceInfo));
        setIsReady(true);

        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'from inject fcm received', value: true }));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
  const ignoredPatterns = [
      'Warning:',
      'React has detected',
      'ResizeObserver loop limit exceeded',
      'Cannot update a component while rendering a different component',
      'act(...)',
      'validateDOMNesting(...)',
      'Script error.',
      'setPhotoOptions failed',      
      'An unexpected error occurred',
    ];

    const shouldIgnore = (message = '') => !message || ignoredPatterns.some((pattern) => message.includes(pattern));

    const onError = (message, source, lineno, colno, error) => {
      if (shouldIgnore(message)) return;
      console.warn('Global error:', message);
      setGlobalError(message || 'A global error occurred.');
    };

    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
          ? reason
          : 'An unexpected error occurred.';

      if (shouldIgnore(message)) return;

      console.warn('Unhandled rejection:', message);
      setGlobalError(message);
    };

    window.onerror = onError;
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  const shouldShowUpdateDialog =
  deviceInfo ? !!getVersion?.version && !!deviceVersion && isServerVersionGreater(getVersion.version, deviceVersion) : (deviceType === 'ios' || deviceType === 'android') ? true : false;

  if (globalError) {
    return <ErrorPage message={globalError} />;
  }
  
  return (
    <div className="App">
      <ErrorBoundary>
      <Toaster />
      <Router />
      <UpdateDialog open={shouldShowUpdateDialog} />
      </ErrorBoundary>
    </div>
  );
}

export default App;
